import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { QueryFunctionContext, QueryStatus } from "react-query"
import { Advertisement, FittingPosition, ListFilter, WholesalerArticle } from "@tm/models"
import { DataSupplierFilter, ProductGroupFilter } from "@bundles/parts/data/model"
import { useRecoilState } from "recoil"
import { isEqual, sortBy } from "lodash"
import { useOffersListParamsContext } from "@bundles/parts/components/ListV2/Offers/ContextProvider"
import { FilterActions, FiltersData, OffersArticleData } from "../../../models"
import {
    QUERY_KEY,
    QueryKey,
    replaceArticlesQuantityWithSuggestedQuantity,
    useArticlesBase,
    useArticleSelectionActions,
    useFilterStorageKey,
    usePartsViewOptions,
} from "../../../hooks"
import { emptyFunc, getArticleGroups, mapDataSupplierFilter, mapDistinctValues, mapProductGroupFilter } from "../../../helpers"
import { DefaultArticleListPageSize, getBundleParams } from "../../../../../utils"
import { fetchArticles, OfferArticleRequest } from "./fetchArticles"
import { SelectedProductGroupIdsAtom, SelectedSupplierIdsAtom } from "../../../states"
import { isExtendedAssortmentForced } from "../../../helpers/isExtendedAssortmentForced"

const CHECK_DELAY = 500

type FiltersAndResultsT = {
    filters: FiltersData & FilterActions
    articleData: OffersArticleData
}

export function useFiltersAndResults(isEnabled: boolean, advertisements: Advertisement[] | undefined, setDefaultSuppliers?: boolean) {
    const {
        startParams,
        fittingPosition,
        setFittingPosition,
        extendedAssortment,
        showAvailable,
        showAvailableSecondary,
        attributes,
        noResult,
        setAvailability,
        setAvailabilitySecondary,
        setProductGroups,
        productGroups,
        suppliers,
        setSuppliers,
    } = useOffersListParamsContext()
    const requestTimerRef = useRef<number>()
    const requestDelay = useRef(0) // First request should be without any delay

    const storageKey = useFilterStorageKey(startParams)
    const {
        partsViewSettings: { quantitySuggestionEnabled },
    } = usePartsViewOptions()

    const [wholesalerArticles, setWholesalerArticles] = useState<WholesalerArticle[] | undefined>(undefined)
    const [prodGroupFilters, setProductGroupFilters] = useState<ProductGroupFilter[] | undefined>(undefined)
    const [dataSupplFilters, setDataSupplierFilters] = useState<DataSupplierFilter[] | undefined>(undefined)

    const [extendedAssortmentEnabledByUser, setExtendedAssortmentEnabledByUser] = useState(!!extendedAssortment)
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(!!showAvailable)
    const [selectedProductGroupIds, setSelectedProductGroupIds] = useRecoilState(SelectedProductGroupIdsAtom(storageKey))
    const [selectedDataSupplierIds, setSelectedSupplierIds] = useRecoilState(SelectedSupplierIdsAtom(storageKey))
    const [showOnlyAvailableSecondary, setShowOnlyAvailableSecondary] = useState(!!showAvailableSecondary)
    const [isFiltered, setIsFiltered] = useState(false)
    const [activeQueryKey, setQueryKey] = useState([QUERY_KEY, selectedProductGroupIds, selectedDataSupplierIds].join())

    const hasActiveFilters = selectedProductGroupIds.length > 0 || selectedDataSupplierIds.length > 0

    const request = useMemo<OfferArticleRequest | null>(() => {
        if (!isEnabled || !startParams.advertisementCategory?.id) {
            return null
        }

        return {
            pageSize: getBundleParams().articleListPageSize ?? DefaultArticleListPageSize,
            advertisements: advertisements ?? [],
        }
    }, [isEnabled, startParams.advertisementCategory?.id, advertisements])

    const [requestStatus, setRequestStatus] = useState<QueryStatus>("idle")

    const articlesBase = useArticlesBase(
        request,
        async ({ queryKey, pageParam, meta }: QueryFunctionContext<QueryKey<OfferArticleRequest>, number>) => {
            const {
                data,
                productGroupFilters: pgFilters,
                dataSupplierFilters: dsFilters,
                wholesalerParts,
                nextPageIndex,
            } = await fetchArticles(
                {
                    queryKey,
                    pageParam,
                    meta,
                },
                selectedProductGroupIds,
                selectedDataSupplierIds
            )

            setProductGroupFilters(pgFilters)
            setDataSupplierFilters(dsFilters)
            setWholesalerArticles(wholesalerParts)
            setIsFiltered(hasActiveFilters)

            return { data, nextPageIndex }
        },
        activeQueryKey,
        quantitySuggestionEnabled ? replaceArticlesQuantityWithSuggestedQuantity : undefined
    )

    const extendedAssortmentForced = isExtendedAssortmentForced(
        { productGroupFilters: prodGroupFilters || [], dataSupplierFilters: dataSupplFilters || [] },
        selectedProductGroupIds,
        selectedDataSupplierIds,
        articlesBase.isSuccess,
        (dataSupplier: DataSupplierFilter) => dataSupplier.isTopPriority
    )

    const extendedAssortmentEnabled = extendedAssortmentEnabledByUser || extendedAssortmentForced

    // Relevant for Multiselect, resets on reload, list change or new search
    const { deselectAll } = useArticleSelectionActions()

    useEffect(() => {
        deselectAll()
    }, [request, showOnlyAvailable, showOnlyAvailableSecondary, fittingPosition])

    useEffect(
        function extendedAssortmentBecauseNoResult() {
            if (!!attributes.length && noResult && !extendedAssortment) {
                setExtendedAssortmentEnabledByUser(true)
            }
        },
        [noResult, extendedAssortment, attributes]
    )

    useEffect(
        function transferAvailabilityToParams() {
            setAvailability(showOnlyAvailable)
        },
        [showOnlyAvailable]
    )

    useEffect(
        function transferAvailabilityToParams() {
            setAvailabilitySecondary(showOnlyAvailableSecondary)
        },
        [showOnlyAvailableSecondary]
    )

    const resetAvailability = useCallback(() => {
        setShowOnlyAvailable(false)
        setShowOnlyAvailableSecondary(false)
    }, [setShowOnlyAvailable, setShowOnlyAvailableSecondary])

    const resetAll = useCallback(() => {
        setSelectedProductGroupIds((prev) => (prev.length ? [] : prev))
        setSelectedSupplierIds((prev) => (prev.length ? [] : prev))
        resetAvailability()
        setFittingPosition?.(FittingPosition.None)
    }, [setSelectedProductGroupIds, setSelectedSupplierIds, resetAvailability, setFittingPosition])

    // Reset if the search params have changed
    useEffect(() => {
        resetAll()
    }, [startParams])

    useEffect(
        function transferSelectedFiltersToParams() {
            if (!prodGroupFilters || !dataSupplFilters) {
                return
            }

            if (!selectedProductGroupIds.length && productGroups.length) {
                setProductGroups((prev) => (prev.length ? [] : prev))
            } else if (!isEqual(sortBy(selectedProductGroupIds), sortBy(productGroups.map((x) => x.id)))) {
                setProductGroups(
                    prodGroupFilters
                        .filter((filter) => selectedProductGroupIds.some((id) => id === filter.id))
                        .map((filter) => mapProductGroupFilter(filter, extendedAssortmentEnabled))
                )
            }

            if (!selectedDataSupplierIds.length && suppliers.length && !setDefaultSuppliers) {
                setSuppliers((prev) => (prev.length ? [] : prev))
            } else if (!isEqual(sortBy(selectedDataSupplierIds), sortBy(suppliers.map((x) => x.id))) && !setDefaultSuppliers) {
                setSuppliers(dataSupplFilters.filter((filter) => selectedDataSupplierIds.some((id) => id === filter.id)).map(mapDataSupplierFilter))
            }

            if (setDefaultSuppliers && !selectedDataSupplierIds.length) {
                setSuppliers((prevState) => {
                    const newSuppliers = dataSupplFilters.map(mapDataSupplierFilter)
                    return isEqual(prevState, newSuppliers) ? prevState : newSuppliers
                })
            }
        },
        [
            prodGroupFilters,
            dataSupplFilters,
            selectedProductGroupIds,
            selectedDataSupplierIds,
            setDefaultSuppliers,
            extendedAssortmentEnabled,
            productGroups,
            suppliers,
            setProductGroups,
            setSuppliers,
        ]
    )

    useEffect(() => {
        window.clearTimeout(requestTimerRef.current)
        // Time-delayed Request
        requestTimerRef.current = window.setTimeout(() => {
            setQueryKey([QUERY_KEY, selectedProductGroupIds, selectedDataSupplierIds].join())
        }, requestDelay.current)

        // Any further request will be delayed (to prevent multiple requests when state changes quickly)
        requestDelay.current = CHECK_DELAY
    }, [selectedProductGroupIds, selectedDataSupplierIds])

    const toggleProductGroup = useCallback((id: number, exclusive?: boolean) => {
        setSelectedProductGroupIds((state) => {
            if (exclusive) {
                if (state.length === 1 && state.includes(id)) {
                    return []
                }

                return [id]
            }

            if (state.includes(id)) {
                return state.filter((x) => x !== id)
            }

            return sortBy([...state, id])
        })
    }, [])

    const toggleSupplier = useCallback((id: number, exclusive?: boolean) => {
        setSelectedSupplierIds((state) => {
            if (exclusive) {
                if (state.length === 1 && state.includes(id)) {
                    return []
                }

                return [id]
            }

            if (state.includes(id)) {
                return state.filter((x) => x !== id)
            }

            return sortBy([...state, id])
        })
    }, [])

    const toggleAvailability = useCallback(() => {
        setShowOnlyAvailable((state) => !state)

        if (showOnlyAvailable === false && showOnlyAvailableSecondary) {
            setShowOnlyAvailableSecondary(false)
        }
    }, [showOnlyAvailable, showOnlyAvailableSecondary])

    const toggleAvailabilitySecondary = useCallback(() => {
        setShowOnlyAvailableSecondary((state) => !state)

        if (showOnlyAvailable && showOnlyAvailableSecondary === false) {
            setShowOnlyAvailable(false)
        }
    }, [showOnlyAvailable, showOnlyAvailableSecondary])

    const productGroupFilters = useMemo<[ListFilter, boolean][]>(() => {
        if (!prodGroupFilters) {
            return []
        }
        return prodGroupFilters.map((filter) => [
            mapProductGroupFilter(filter, extendedAssortmentEnabled),
            selectedProductGroupIds.includes(filter.id),
        ])
    }, [prodGroupFilters, extendedAssortmentEnabled, selectedProductGroupIds])

    const dataSupplierFilters = useMemo<[ListFilter, boolean][]>(() => {
        if (!dataSupplFilters) {
            return []
        }
        return dataSupplFilters.map((filter) => [mapDataSupplierFilter(filter), selectedDataSupplierIds.includes(filter.id)])
    }, [selectedDataSupplierIds, dataSupplFilters])

    const { isSuccess, articles, hasNextPage } = articlesBase

    const articleData = useMemo<OffersArticleData>(() => {
        const supplierIds = mapDistinctValues(articles, (x) => x.supplier.id)
        const productGroupIds = mapDistinctValues(articles, (x) => x.productGroup.id)
        const articleGroups = getArticleGroups(articles, hasNextPage)

        return {
            ...articlesBase,
            isEnabled,
            isFrontendFiltered: isFiltered,
            requestStatus,
            articleGroups,
            wholesalerArticles,
            supplierIds,
            productGroupIds,
        }
    }, [articles, hasNextPage, isEnabled, isFiltered, requestStatus, wholesalerArticles]) // Please don't add articlesBase

    useLayoutEffect(
        function waitUntilRendered() {
            if (isSuccess) {
                setRequestStatus("success")
            }
        },
        [isSuccess]
    )

    const showFittingPositionsFilter = useMemo(() => productGroups.some((productGroup) => !!productGroup.hasFittingSideFilters), [productGroups])

    const resetProductGroups = useCallback(() => setSelectedProductGroupIds((prev) => (prev.length ? [] : prev)), [])
    const resetSuppliers = useCallback(() => setSelectedSupplierIds((prev) => (prev.length ? [] : prev)), [])

    return useMemo<FiltersAndResultsT>(
        () => ({
            filters: {
                showOnlyAvailable,
                showOnlyAvailableSecondary,
                productGroupFilters,
                dataSupplierFilters,
                attributeFilters: [],
                groupedAttributeFilters: [],
                extendedAssortment: {
                    enabled: extendedAssortmentEnabledByUser,
                    forced: extendedAssortmentForced,
                },
                fittingPosition,
                isLoading: false,
                isRefetching: false,
                showFittingPositionsFilter,
                showExtendedAssortmentFilter: false,
                toggleProductGroup,
                toggleSupplier,
                toggleExtendedAssortment: emptyFunc,
                toggleAvailability,
                toggleAvailabilitySecondary,
                setFittingPosition,
                resetProductGroups,
                resetSuppliers,
                resetAvailability,
                resetAll,
            },
            articleData,
        }),
        [
            articleData,
            dataSupplierFilters,
            extendedAssortmentEnabledByUser,
            extendedAssortmentForced,
            fittingPosition,
            productGroupFilters,
            resetAll,
            resetAvailability,
            resetProductGroups,
            resetSuppliers,
            setFittingPosition,
            showFittingPositionsFilter,
            showOnlyAvailable,
            showOnlyAvailableSecondary,
            toggleAvailability,
            toggleAvailabilitySecondary,
            toggleProductGroup,
            toggleSupplier,
        ]
    )
}
